<script setup lang="ts">
import { boolean, object, string } from 'zod'
import { useField, useForm } from 'vee-validate'
import { toTypedSchema } from '@vee-validate/zod'
import {
    additionalMailFields,
    checkboxes,
    formData,
    inputLabel,
    secondInputLabel,
    socialLinks,
    sohoAdditionalMailFields,
    tabNames,
} from './data'

import type { ISocialLink, TChecked, TChecks } from './interfaces'
import lpFormLegal from '~/constants/lpFormLegal'

defineProps({
    hideForm: Boolean,
})

const route = useRoute()
const isSoho = computed(() => {
    return !!route.path.includes('dla-firm')
})
const currentTab = ref(tabNames.value[0].id)

const formTitle = ref(null)
const isVisible = useElementVisibility(formTitle)

const isLegalUnfolded = ref(false)

const schema = computed(() => {
    let baseSchema = object({
        number: string({
            required_error: 'Wpisz numer telefonu',
        }).regex(/^\d{3} \d{3} \d{3}$/, 'Numer telefonu jest za krótki'),

        checked1: boolean().refine((val) => val === true, {
            message: 'Pole musi być zaznaczone',
        }),
        checked2: boolean().refine((val) => val === true, {
            message: 'Pole musi być zaznaczone',
        }),
    })

    if (currentTab.value === 'umowa') {
        baseSchema = baseSchema.extend({
            secondNumber: string({
                required_error: 'Wpisz numer telefonu',
            }).regex(/^\d{3} \d{3} \d{3}$/, 'Numer telefonu jest za krótki'),
        })
    }

    return toTypedSchema(baseSchema)
})

const { handleSubmit, errors, resetForm } = useForm({
    validationSchema: schema.value,
})

const { value: number } = useField<string>('number')
const { value: secondNumber } = useField<string>('secondNumber')

const checked = reactive<TChecked>({
    checked1: { val: false, disabled: false },
    checked2: { val: false, disabled: false },
})

const { value: checked1 } = useField('checked1')
const { value: checked2 } = useField('checked2')

watch(
    () => checked.checked1.val,
    (newVal) => (checked1.value = newVal),
)
watch(
    () => checked.checked2.val,
    (newVal) => (checked2.value = newVal),
)

const submitted = ref(false)
const success = ref(false)
const warning = ref(false)
const pending = ref(false)

function resetFormStates() {
    resetForm()
    checked.checked1.val = false
    checked.checked2.val = false
    submitted.value = false
    success.value = false
    warning.value = false
}

function retryForm() {
    resetFormStates()
    warning.value = false
}

const legalItems = computed(() => [
    {
        value: 'item-1',
        title: `Pełne informacje na temat przetwarzania danych osobowych <strong class="ml-1 underline">${isLegalUnfolded.value ? 'Zwiń' : 'Rozwiń'}</strong>`,
        content: '',
    },
])

const { sendLeadForm } = useLeadForm()
const { sendEvent } = useSendEvent()

const onSubmit = handleSubmit(async () => {
    pending.value = true

    let tabAdditionalMailFields

    if (currentTab.value === 'umowa') {
        tabAdditionalMailFields = isSoho.value ? sohoAdditionalMailFields.Sprzet : additionalMailFields.Sprzet
    } else if (currentTab.value === 'abo') {
        tabAdditionalMailFields = isSoho.value ? sohoAdditionalMailFields.Abonament : additionalMailFields.Abonament
    } else if (currentTab.value === 'internet') {
        tabAdditionalMailFields = isSoho.value ? sohoAdditionalMailFields.Internet : additionalMailFields.Internet
    } else {
        return
    }

    tabAdditionalMailFields = {
        ...tabAdditionalMailFields,
        NR_KONTAKT: number.value,
        NR_GLOWNY: secondNumber.value,
        ZGODA1: 'TAK',
        ZGODA_PKE: 'TAK',
    }

    try {
        const response = await sendLeadForm(tabAdditionalMailFields)

        pending.value = response.pending
        success.value = response.success
        warning.value = response.warning

        if (success.value) {
            sendEvent({
                category: 'lp-homepage_footer',
                label: `${currentTab.value}_${response.resp?.sendLead}`,
                action: response.warning ? 'form_no_send' : 'form_send',
                click_text: `Wyślij_${currentTab.value}`,
                click_element: 'button',
                component_name: 'footer',
            })
        }
    } catch (err) {
        console.error('[PLUS BLOG LANDINGPAGES - FORM FOOTER FORM] - error during form submission:', err)
        sendEvent({
            category: 'lp-homepage_footer',
            label: `${currentTab.value}_error`,
            action: `form_no_send`,
            click_text: `Wyślij_${currentTab.value}`,
            click_element: 'button',
            component_name: 'footer',
        })
    }
})

function setSubmitted() {
    submitted.value = true
}

const isError = computed(() => {
    return submitted.value && Object.values(checked).some((val) => !val.val)
})

function isErrorForCheckbox(checkboxNumber: number) {
    const checkboxKey = `checked${checkboxNumber}` as TChecks
    const checkbox = checked[checkboxKey]

    return submitted.value && !checkbox.val
}

function handleTriggerClick() {
    isLegalUnfolded.value = !isLegalUnfolded.value
    sendEvent({
        category: 'lp-homepage_footer',
        click_text: isLegalUnfolded.value ? 'rozwiń' : 'zwiń',
        click_element: 'text',
        component_name: 'footer',
    })
}

function sendSocialGA(social: ISocialLink) {
    sendEvent({
        category: 'lp-homepage_footer',
        label: social.alt,
        action: `click_${social.link}`,
        click_path: social.link,
        click_text: social.alt,
        click_element: 'icon',
        component_name: 'footer',
    })
}

function sendFormGA() {
    sendEvent({
        category: 'lp-homepage_footer',
        label: 'Formularz',
        action: `click_http://www.bok.plus.pl/form`,
        click_path: 'http://www.bok.plus.pl/form',
        click_text: 'Formularz',
        click_element: 'link',
        component_name: 'footer',
    })
}

function sendCheckboxGa(value: boolean, checkbox: string = 'all') {
    const label =
        checkbox === 'all' ? `zaznacz wszystkie zgody_${value ? 'on' : 'off'}` : `${checkbox}_${value ? 'on' : 'off'}`

    sendEvent({
        category: 'lp-homepage_footer',
        label,
        action: `click_http://www.bok.plus.pl/form`,
        click_text: label,
        click_element: 'checkbox',
        component_name: 'footer',
    })
}

watch(currentTab, (value) => {
    resetFormStates()
    const clickedText =
        value === 'abo'
            ? 'Abonament i sprzęt'
            : value === 'internet'
              ? 'Internet'
              : value === 'umowa'
                ? 'Przedłuż lub zmień umowę'
                : ''
    sendEvent({
        category: 'lp-homepage_footer',
        label: 'click',
        action: clickedText,
        click_text: clickedText,
        click_element: 'tab',
        component_name: 'footer',
    })
})

watch(isVisible, (newValue) => {
    if (newValue) {
        sendEvent({
            category: 'lp-homepage_footer',
            label: 'visible',
            action: 'footer',
            component_name: 'footer',
        })
    }
})
</script>

<template>
    <div
        class="expand 0.5s forwards relative flex w-full flex-col gap-6 rounded-2xl bg-[url('https://api.plus.pl/file/images_lp/plus_acse_landingpages/Footer/tlo_mobile_1800.webp')] bg-[100%_0] bg-no-repeat object-cover p-6 font-figtree delay-0 duration-400 ease-in-out xl:min-h-[531px] xl:max-w-[807px] xl:flex-row xl:bg-transparent xl:bg-[url('https://api.plus.pl/file/images_lp/plus_acse_landingpages/Footer/tlo_desktop_1800.webp')]"
    >
        <div
            v-if="!hideForm"
            class="flex-1 xl:p-4"
        >
            <div
                v-if="!warning && !success"
                :class="warning || success ? 'animate-lp-fade-out' : ''"
            >
                <h3
                    class="lp-heading-sm mb-2 font-bold leading-7 text-plus-lp-dark-blue xl:lp-heading-xl"
                    v-html="formData.title"
                />
                <span class="lp-label-md text-plus-lp-dark-blue-400">{{ formData.subtitle }}</span>
                <div class="mt-3 text-plus-lp-dark-blue-400 xl:max-w-[420px]">
                    <form @submit.prevent="onSubmit">
                        <p class="lp-paragraph-lg mb-2 font-medium text-plus-lp-dark-blue-500">Wybierz temat rozmowy</p>
                        <PlusBlogLpMoleculeTabsContainer
                            v-model="currentTab"
                            :footer-tab="true"
                            :tab-names="tabNames"
                            :initial-active-tab-id="currentTab"
                            small
                        >
                            <div class="mt-2 flex flex-col gap-4">
                                <PlusBlogLpAtomInput
                                    id="footer-form-input-1"
                                    v-model:model-value="number"
                                    placeholder="000 000 000"
                                    :label="inputLabel"
                                    phone-number
                                    type="text"
                                    max="11"
                                    :error-message="errors.number"
                                    :validation="submitted && !!errors.number"
                                />
                                <PlusBlogLpAtomInput
                                    v-if="currentTab === 'umowa'"
                                    id="footer-form-input-2"
                                    v-model:model-value="secondNumber"
                                    placeholder="000 000 000"
                                    :label="secondInputLabel"
                                    phone-number
                                    type="text"
                                    max="11"
                                    :error-message="errors.secondNumber"
                                    :validation="submitted && !!errors.secondNumber"
                                />
                            </div>
                        </PlusBlogLpMoleculeTabsContainer>
                        <PlusBlogLpMoleculeCheckboxGroup
                            v-model:group="checked"
                            :error="isError"
                            @update:checkbox="sendCheckboxGa($event)"
                        >
                            <PlusBlogLpAtomCheckbox
                                v-for="checkbox in checkboxes"
                                :key="checkbox.id"
                                v-model:checked="checked[checkbox.id].val"
                                :error="isErrorForCheckbox(checkbox.checkboxNumber)"
                                required
                                :error-message="checkbox.error"
                                @update:checked="sendCheckboxGa($event, checkbox.gaEventText)"
                            >
                                <template #label>
                                    {{ checkbox.label }}
                                </template>
                            </PlusBlogLpAtomCheckbox>
                        </PlusBlogLpMoleculeCheckboxGroup>

                        <PlusBlogLpAtomFormLegal
                            :form-legal="lpFormLegal"
                            :accordion-items="legalItems"
                            default-value=""
                            class="pr-5"
                            @trigger-click="handleTriggerClick"
                        />

                        <PlusBlogLpAtomBaseButton
                            variant="primary"
                            arrow
                            type="submit"
                            block
                            :loading="pending"
                            class="ml-auto mt-4 xl:w-[180px]"
                            @click="setSubmitted"
                        >
                            Wyślij
                        </PlusBlogLpAtomBaseButton>
                    </form>
                </div>
            </div>
            <PlusBlogLpMoleculeFormWarningForm
                v-if="warning"
                class="animate-lp-fade-in"
                @retry-form="retryForm"
            />
            <PlusBlogLpMoleculeFormSuccessForm
                v-if="success"
                class="animate-lp-fade-in"
            />
        </div>
        <div
            class="bg-small max-h-[483px] rounded-2xl bg-plus-lp-grey-100 px-4 py-6 xl:flex xl:w-[284px] xl:flex-col xl:justify-between xl:bg-transparent xl:bg-[url('https://api.plus.pl/file/images_lp/plus_acse_landingpages/Footer/Rectangle2.webp')] xl:px-6 xl:pt-10"
        >
            <div class="xl:w-[236px]">
                <img
                    src="https://api.plus.pl/file/images_lp/plus_acse_landingpages/IPlus/Vector_(3).webp"
                    alt="icon"
                    class="hidden xl:block"
                />
                <hr
                    size="1"
                    width="100%"
                    noshade
                    class="text-plus-lp-brand-darkblue-300 my-6 hidden xl:my-8 xl:block"
                />
                <p class="lp-label-md mb-2 text-plus-lp-dark-blue">{{ formData.text }}</p>
                <div class="mb-2">
                    <a href="tel:+48 601 100 601">
                        <span class="lp-heading-sm font-medium text-plus-lp-dark-blue">
                            {{ formData.phoneNumber }} *
                        </span>
                    </a>
                </div>
                <span class="text-plus-lp-dark-blue">
                    lub skontaktuj się z Internetową Obsługą klienta przez
                    <a href="https://bok.plus.pl/form">
                        <span
                            class="underline"
                            @click="sendFormGA"
                        >
                            formularz
                        </span>
                    </a>
                </span>
                <p class="lp-paragraph-sm mt-2 text-plus-lp-dark-blue-400">{{ formData.infoSubtext }}</p>
            </div>
            <div class="mt-12 xl:mt-0">
                <ul class="flex justify-end gap-1">
                    <li
                        v-for="link in socialLinks"
                        :key="link.id"
                        @click="sendSocialGA(link)"
                    >
                        <a
                            :href="link.link"
                            target="_blank"
                        >
                            <img
                                :src="link.img"
                                :alt="link.alt"
                                class="size-8"
                            />
                        </a>
                    </li>
                </ul>
            </div>
        </div>
    </div>
</template>
